<template>
  <div class="animal-habitat-game">
    <LevelComplete v-if="gameOver" />
    <!-- Habitat container -->
    <div
      v-if="currentHabitat"
      class="habitat-targets"
    >
      <div
        class="habitat-target"
        :class="currentHabitat.name.toLowerCase() + '-target'"
        :style="{
          backgroundImage: 'url(' + currentHabitat.backgroundImage + ')',
        }"
        @dragover.prevent
        @drop="handleDrop($event, currentHabitat.name)"
        @click="speakOutLoud(currentHabitat.name)"
      >
        <h2 class="habitat-label">
          {{ currentHabitat.name }}
        </h2>
        <!-- Display the correctly dropped items -->
        <div class="dropped-items">
          <span
            v-for="item in correctDrops[currentHabitat.name]"
            :key="item.id"
          >{{ item.emoji }}</span>
        </div>
      </div>
    </div>

    <!-- Draggable animals -->
    <div class="draggable-items">
      <div
        v-for="(item) in draggableItems"
        :key="item.id"
        class="draggable-emoji"
        :style="draggedItem === item ? draggedStyle : {}"
        draggable="true"
        @dragstart="handleDrag($event, item)"
        @click="speakOutLoud(item.name)"
        @touchstart="handleTouchStart($event, item)"
        @touchmove="handleTouchMove($event)"
        @touchend="handleTouchEnd($event)"
      >
        {{ item.emoji }}
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    const habitatsList = [
      {
        name: "Forest",
        backgroundImage:
          "https://img.freepik.com/premium-photo/forest-scene-with-various-forest-trees-kids-story_911078-2375.jpg",
      },
      {
        name: "Ocean",
        backgroundImage:
          "https://i.pinimg.com/736x/02/6b/99/026b998ac845e8fa6d0ad976ba9172ba.jpg",
      },
      {
        name: "Desert",
        backgroundImage: "https://m.media-amazon.com/images/I/61LTJ027bAL.jpg",
      },
      {
        name: "Arctic",
        backgroundImage:
          "https://static.vecteezy.com/system/resources/thumbnails/014/496/474/small_2x/winter-illustration-design-winter-landscape-with-elegance-concept-free-vector.jpg",
      },
      {
        name: "Jungle",
        backgroundImage:
          "https://img.freepik.com/premium-vector/dense-green-tropical-forest-fresh-river_43633-12069.jpg",
      },
    ];
    const animalEmojis = [
      // Forest animals
      { id: 1, emoji: "🦌", habitat: "Forest", name: "Deer" },
      { id: 2, emoji: "🦉", habitat: "Forest", name: "Owl" },
      { id: 3, emoji: "🐿️", habitat: "Forest", name: "Squirrel" },
      // Ocean animals
      { id: 4, emoji: "🐳", habitat: "Ocean", name: "Whale" },
      { id: 5, emoji: "🐙", habitat: "Ocean", name: "Octopus" },
      { id: 6, emoji: "🐬", habitat: "Ocean", name: "Dolphin" },
      // Desert animals
      { id: 7, emoji: "🐪", habitat: "Desert", name: "Camel" },
      { id: 8, emoji: "🦂", habitat: "Desert", name: "Scorpion" },
      { id: 9, emoji: "🦎", habitat: "Desert", name: "Lizard" },
      // Arctic animals
      { id: 10, emoji: "🐧", habitat: "Arctic", name: "Penguin" },
      { id: 11, emoji: "🐻‍❄️", habitat: "Arctic", name: "Polar Bear" },
      { id: 12, emoji: "🦭", habitat: "Arctic", name: "Seal" },
      // Jungle animals
      { id: 13, emoji: "🦁", habitat: "Jungle", name: "Lion" },
      { id: 14, emoji: "🦧", habitat: "Jungle", name: "Orangutan" },
      { id: 15, emoji: "🦜", habitat: "Jungle", name: "Parrot" },
    ];
    const correctDrops = {};
    const totalItemsPerHabitat = {};
    habitatsList.forEach((habitat) => {
      correctDrops[habitat.name] = [];
      totalItemsPerHabitat[habitat.name] = 0;
    });
    animalEmojis.forEach((item) => {
      totalItemsPerHabitat[item.habitat]++;
    });

    return {
      habitatsList,
      animalEmojis,
      draggableItems: [],
      totalItemsPerHabitat,
      message: "",
      correctDrops,
      // Variables for touch events
      draggedItem: null,
      draggedStyle: {},
      draggedItemWidth: 0,
      draggedItemHeight: 0,
      selectedVoice: null,
      currentHabitatIndex: 0,
      gameOver: false,
    };
  },
  computed: {
    currentHabitat() {
      if (this.gameOver) return null;
      return this.habitatsList[this.currentHabitatIndex];
    },
  },
  mounted() {
    // Initialize draggable items for the first habitat
    this.setupDraggableItems();

    // Setup speech synthesis voices
    if (typeof speechSynthesis === "undefined") {
      console.warn("Speech Synthesis API not supported in this browser.");
    } else {
      speechSynthesis.onvoiceschanged = () => this.setupVoices();
    }
    this.setupVoices();
  },
  methods: {
    handleDrag(event, item) {
      event.dataTransfer.setData("text/plain", JSON.stringify(item));
    },
    handleDrop(event, targetHabitat) {
      const itemData = event.dataTransfer.getData("text/plain");
      const item = JSON.parse(itemData);
      if (item.habitat === targetHabitat) {
        if (!this.correctDrops[targetHabitat].some((i) => i.id === item.id)) {
          this.correctDrops[targetHabitat].push(item);
          this.playSound(
            "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
          );
          // Remove the item from draggableItems
          this.draggableItems = this.draggableItems.filter(
            (i) => i.id !== item.id
          );
          // Speak the animal's name
          this.speakOutLoud(item.name);
          // Check if the habitat is complete
          if (
            this.correctDrops[targetHabitat].length ===
            this.totalItemsPerHabitat[targetHabitat]
          ) {
            // Habitat completed
            this.currentHabitatIndex++;
            if (this.currentHabitatIndex >= this.habitatsList.length) {
              // All habitats completed
              this.message = "🎉 Great Job!";
              this.gameOver = true;
              this.speakOutLoud("Great Job!");
              setTimeout(() => {
                this.$router.push("/animals");
              }, 4000);
            } else {
              // Setup draggable items for the next habitat
              this.setupDraggableItems();
              this.message = "✅";
              setTimeout(() => {
                this.message = "";
              }, 2000);
            }
          }
        } else {
          this.message = "Already placed!";
          setTimeout(() => {
            this.message = "";
          }, 2000);
        }
      } else {
        this.message = "❌ Try again!";
        this.speakOutLoud("Try again!");
        // Clear message after 2 seconds
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
    },
    handleTouchStart(event, item) {
      const touch = event.touches[0];
      this.draggedItem = item;
      this.draggedItemWidth = event.target.offsetWidth;
      this.draggedItemHeight = event.target.offsetHeight;
      // Position the item directly under the finger
      this.draggedStyle = {
        position: "fixed",
        left: `${touch.clientX - this.draggedItemWidth / 2}px`,
        top: `${touch.clientY - this.draggedItemHeight / 2}px`,
        zIndex: 1000,
        fontSize: "5rem",
      };
    },
    handleTouchMove(event) {
      event.preventDefault(); // Prevent scrolling
      const touch = event.touches[0];
      if (this.draggedItem) {
        // Update the position to keep the item under the finger
        this.draggedStyle.left = `${touch.clientX - this.draggedItemWidth / 2}px`;
        this.draggedStyle.top = `${touch.clientY - this.draggedItemHeight / 2}px`;
      }
    },
    handleTouchEnd(event) {
      const dropZone = this.$el.querySelector(".habitat-target");
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      let dropped = false;

      if (dropZone) {
        const rect = dropZone.getBoundingClientRect();
        if (
          touchEndX >= rect.left &&
          touchEndX <= rect.right &&
          touchEndY >= rect.top &&
          touchEndY <= rect.bottom
        ) {
          const targetHabitat = this.currentHabitat.name;
          this.handleDrop(
            {
              dataTransfer: { getData: () => JSON.stringify(this.draggedItem) },
            },
            targetHabitat
          );
          dropped = true;
        }
      }

      if (!dropped) {
        this.message = "❌ Try again!";
        this.speakOutLoud("Try again!");
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }

      this.draggedItem = null;
      this.draggedStyle = {};
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    setupDraggableItems() {
      if (!this.currentHabitat) return;

      const correctItems = this.animalEmojis.filter(
        (item) => item.habitat === this.currentHabitat.name
      );

      const wrongItemsPool = this.animalEmojis.filter(
        (item) => item.habitat !== this.currentHabitat.name
      );

      // Determine how many wrong items to add
      const maxDraggable = 6;
      const neededWrong = Math.min(
        maxDraggable - correctItems.length,
        wrongItemsPool.length
      );

      // Shuffle wrongItemsPool
      const shuffledWrong = wrongItemsPool.sort(() => Math.random() - 0.5);

      const selectedWrong = shuffledWrong.slice(0, neededWrong);

      // Combine correct and wrong items
      const combinedItems = [...correctItems, ...selectedWrong];

      // Shuffle combined items
      this.draggableItems = combinedItems.sort(() => Math.random() - 0.5);
    },
  },
};
</script>

<style scoped>
.animal-habitat-game {
  position: relative;
  width: 100%;
  height: 100vh;
}

/* Habitat targets */
.habitat-targets {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.habitat-target {
  width: 80%;
  height: 200px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 10px;
  position: relative;
  text-align: center;
  padding-top: 10px;
  cursor: pointer;
  margin: auto;
}

.habitat-target .habitat-label {
  position: absolute;
  width: 100%;
  text-align: center;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 2rem;
  font-weight: bold;
  color: white;
  text-transform: capitalize;
  text-shadow: 2px 2px 4px #000000;
}

.dropped-items {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  padding: 5px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: start;
}

.dropped-items span {
  font-size: 3rem;
  margin: 5px;
}

/* Messages */
.message {
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
}

/* Draggable items */
.draggable-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: auto;
  width: 80%;
}

.draggable-emoji {
  cursor: grab;
  font-size: 5rem;
  margin: 1rem;
  transition: transform 0.3s;
  position: relative;
}

.draggable-emoji:hover {
  transform: scale(1.2);
}

@media (hover: none) {
  .draggable-emoji:hover {
    transform: none;
  }
}
</style>